/**
 * Live μDock session control plane (W1). Never invents RTSP bytes.
 * Soft≠Live Page156: posture bus — operator language only (no path chips).
 */
import { path, api } from "./api.js";

/** Soft operator errors — never paint wire paths or feature-flag tokens. */
export function softUdockErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "Live video could not be opened.";
  if (/udock_live_disabled|live.?disabled|not armed|503/.test(low)) {
    return "Live video is not armed on this site yet.";
  }
  if (/\b403\b|not_authorized|forbidden|insufficient/.test(low)) {
    return "Your account cannot open live video on this unit.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|lookuperror/.test(low)) {
    return "That unit or session is not available.";
  }
  if (/\b429\b|quota|full/.test(low)) {
    return "Too many live sessions right now. Try again shortly.";
  }
  if (/\b400\b|operator_reason|invalid/.test(low)) {
    return "That live request was not accepted.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/devices\/|\/udock|live-sessions|http|GET |POST /.test(low)) {
    return "Live video could not be opened.";
  }
  return "Live video could not be opened.";
}

/** Always-on Live posture — never Soft invents remux when unarmed. */
export function getUdockStatus() {
  return call(path("get__udock_status"));
}

async function call(url, opts) {
  try {
    return { ok: true, data: await api(url, opts || {}) };
  } catch (e) {
    const msg = String((e && e.message) || e);
    return { ok: false, error: softUdockErrorOf(msg), raw: msg };
  }
}

export function openLiveSession(
  deviceId,
  {
    operator_reason,
    ttl_s = 300,
    camera_hint = "",
    camera_id = "",
    alarm_id = null,
    trigger_kind = "",
  } = {},
) {
  if (String(operator_reason || "").trim().length < 8) {
    return Promise.resolve({
      ok: false,
      error: "Please say why you are opening live video (a short note is enough).",
    });
  }
  const body = {
    operator_reason: String(operator_reason).trim(),
    ttl_s,
    camera_hint: camera_hint || "",
  };
  if (camera_id) body.camera_id = String(camera_id);
  if (alarm_id != null && alarm_id !== "") body.alarm_id = Number(alarm_id);
  if (trigger_kind) body.trigger_kind = String(trigger_kind);
  return call(path("post__devices_p_live-sessions", { pathParams: [deviceId] }), {
    method: "POST",
    body,
  });
}

export function listLiveSessions(deviceId) {
  return call(path("get__devices_p_live-sessions", { pathParams: [deviceId] }));
}

export function getLiveSession(deviceId, sessionId) {
  return call(path("get__devices_p_live-sessions_p", { pathParams: [deviceId, sessionId] }));
}

export function renewLiveSession(deviceId, sessionId, operator_reason) {
  return call(path("post__devices_p_live-sessions_p_renew", { pathParams: [deviceId, sessionId] }), {
    method: "POST",
    body: { operator_reason: String(operator_reason || "Renew live session · console").trim() },
  });
}

export function closeLiveSession(deviceId, sessionId, operator_reason) {
  return call(path("post__devices_p_live-sessions_p_close", { pathParams: [deviceId, sessionId] }), {
    method: "POST",
    body: { operator_reason: String(operator_reason || "Close live session · console").trim() },
  });
}

/** Soft≠Live: pull demuxed AUs for WebCodecs. Query names match the router. */
export function pullLiveViewAus(
  deviceId,
  sessionId,
  { max_n = 16, after_seq = 0, after_ms = 0, wait_ms = 0, view_token = "" } = {},
) {
  const query = { max_n: Number(max_n) || 16 };
  if (after_seq) query.after_seq = Number(after_seq) || 0;
  if (after_ms) query.after_ms = Number(after_ms) || 0;
  if (wait_ms) query.wait_ms = Number(wait_ms) || 0;
  if (view_token) query.view_token = String(view_token);
  return call(
    path("get__devices_p_live-sessions_p_view_aus", {
      pathParams: [deviceId, sessionId],
      query,
    }),
  );
}

/** Optional portless WHEP ticket. WebCodecs AU stays primary. */
export function mintWhepSentryTicket(deviceId, sessionId) {
  return call(
    path("post__devices_p_live-sessions_p_whep-ticket", {
      pathParams: [deviceId, sessionId],
    }),
    { method: "POST", body: {} },
  );
}

/** Soft≠Live: pull opaque remux chunks (base64). Never invents a Soft player. */
export function pullLiveBridge(deviceId, sessionId, max_chunks = 8) {
  return call(
    path("get__devices_p_live-sessions_p_bridge", {
      pathParams: [deviceId, sessionId],
      query: { max_chunks },
    }),
  );
}

/** LQ-4 Soft-honest headroom — GET /live-quota/me; never invents free remux seats. */
export function getLiveQuotaMe({ device_id = "", session_id = "" } = {}) {
  const query = {};
  if (device_id) query.device_id = String(device_id);
  if (session_id) query.session_id = String(session_id);
  return call(path("get__live-quota_me", { query }));
}
